import React, { useEffect, useMemo, useState } from "react";
import Icon from "components/common/Icon";
import { useAuthInfo } from "context/AuthInfoContext";
import { useOptionalToast } from "context/ToastContext";
import { useToken } from "hooks/useToken";
import ActivationDialog from "features/access/components/ActivationDialog";
import { endActivation } from "features/access/services/activationService";
import { activatableRoles, formatRemaining, nextActivationExpiry } from "utils/capabilities";

const TICK_MS = 30000;

// De klok voor de resterende tijd: tikt enkel zolang er een activatie loopt.
function useNow(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

/**
 * Issue #23 (rechten F2 §4): de eigen activeerbare rollen in de topbalk.
 *  - lopend: chip "Beheer · nog 1 u 25 min" met de knop Beëindigen;
 *  - niet lopend: chip "Beheer niet actief — Activeren" (uren ≤ 8 + optionele reden).
 * Bij het verlopen herlaadt auth/me stil (knoppen vergrendelen meteen; utils/capabilities kijkt ook zelf naar de klok).
 * Bewust in de topbalk zelf en niet in het accountmenu: dat menu sluit bij een klik erbuiten, ook in de dialoog.
 */
export default function ActivationControl() {
  const { access, refresh } = useAuthInfo() || {};
  const tokenProvider = useToken();
  const { addToast } = useOptionalToast();
  const initial = useMemo(() => activatableRoles(access), [access]);
  const anyRunning = initial.some((r) => r.running);
  const now = useNow(anyRunning);
  const roles = useMemo(() => activatableRoles(access, now), [access, now]);
  const [target, setTarget] = useState(null);
  const [ending, setEnding] = useState(null);

  // Herladen op het moment dat de eerste lopende activatie afloopt.
  const expiry = useMemo(() => nextActivationExpiry(access), [access]);
  useEffect(() => {
    if (expiry == null) return undefined;
    const delay = Math.max(0, expiry - Date.now()) + 1000;
    const t = setTimeout(() => refresh?.({ silent: true }), Math.min(delay, 2147483647));
    return () => clearTimeout(t);
  }, [expiry, refresh]);

  if (roles.length === 0) return null;

  const end = async (role) => {
    setEnding(role.idAssignment);
    try {
      await endActivation(tokenProvider, { idAssignment: role.idAssignment });
      addToast({ type: "success", title: `${role.name} beëindigd`, message: "De rol telt niet meer mee tot je hem opnieuw activeert." });
      refresh?.({ silent: true });
    } catch (e) {
      addToast({ type: "error", title: "Niet beëindigd", message: e?.message || String(e) });
    } finally {
      setEnding(null);
    }
  };

  const activated = (role) => {
    setTarget(null);
    addToast({ type: "success", title: `${role.name} geactiveerd`, message: "Je rechten zijn bijgewerkt." });
    refresh?.({ silent: true });
  };

  return (
    <>
      <div className="app-activation" role="group" aria-label="Activeerbare rollen">
        {roles.map((r) =>
          r.running ? (
            <span key={r.idAssignment} className="app-activation__chip" title={`Geactiveerd tot ${new Date(r.expiresAt).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}`}>
              <Icon name="shield" size={13} />
              <span>
                {r.name} · nog {formatRemaining(r.expiresAt, now)}
              </span>
              <button
                type="button"
                className="app-activation__end"
                onClick={() => end(r)}
                disabled={ending === r.idAssignment}
                aria-label={`${r.name} beëindigen`}
              >
                {ending === r.idAssignment ? "Bezig…" : "Beëindigen"}
              </button>
            </span>
          ) : (
            // Issue #23 (landing): een duidelijke chip "Beheer niet actief — Activeren", zodat wie de rol nog niet
            // activeerde ziet waarom knoppen vergrendeld zijn en waar hij ze aanzet.
            <span key={r.idAssignment} className="app-activation__chip app-activation__chip--idle" title={`${r.name} telt pas na activeren (max. 8 uur)`}>
              <Icon name="shield" size={13} />
              <span>{r.name} niet actief —</span>
              <button
                type="button"
                className="app-activation__end app-activation__start"
                onClick={() => setTarget(r)}
                aria-label={`${r.name} activeren`}
              >
                Activeren
              </button>
            </span>
          )
        )}
      </div>
      <ActivationDialog
        open={Boolean(target)}
        role={target}
        tokenProvider={tokenProvider}
        onActivated={() => activated(target)}
        onCancel={() => setTarget(null)}
      />
    </>
  );
}
